import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTags,
  getChannelTags,
  getTagChannels,
  searchTags,
  createTag,
  updateTag,
  deleteTag,
  assignTagsToChannels,
  removeAllTagsFromChannels
} from '@/services/tags';
import { toast } from 'sonner';

// Query hooks
export function useTags(providerId: number, versionId: number, regionId: number) {
  return useQuery({
    queryKey: ['tags', providerId, versionId, regionId],
    queryFn: () => getTags(providerId, versionId, regionId),
    enabled: !!(providerId && versionId && regionId)
  });
}

export function useChannelTags(channelIds: number[]) {
  return useQuery({
    queryKey: ['channel-tags', channelIds],
    queryFn: () => getChannelTags(channelIds),
    enabled: channelIds.length > 0
  });
}

export function useTagChannels(tagId: number, providerId: number, versionId: number) {
  return useQuery({
    queryKey: ['tag-channels', tagId, providerId, versionId],
    queryFn: () => getTagChannels(tagId, providerId, versionId),
    enabled: !!(tagId && providerId && versionId)
  });
}

export function useSearchTags(
  providerId: number, 
  versionId: number, 
  regionId: number, 
  searchTerm: string
) {
  return useQuery({
    queryKey: ['search-tags', providerId, versionId, regionId, searchTerm],
    queryFn: () => searchTags(providerId, versionId, regionId, searchTerm),
    enabled: !!(providerId && versionId && regionId && searchTerm.trim().length > 0)
  });
}

// Mutation hooks
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTag,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tags', variables.providerId, variables.versionId, variables.regionId] });
      toast.success(`Tag "${data.name}" created successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create tag');
    }
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTag,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['tag-channels'] });
      toast.success(`Tag "${data.name}" updated successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update tag');
    }
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['channel-tags'] });
      queryClient.invalidateQueries({ queryKey: ['tag-channels'] });
      toast.success('Tag deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete tag');
    }
  });
}

export function useAssignTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignTagsToChannels,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['channel-tags'] });
      queryClient.invalidateQueries({ queryKey: ['tag-channels'] });
      
      const operationText = variables.operation === 'add' ? 'assigned to' : 
                           variables.operation === 'remove' ? 'removed from' : 
                           'replaced for';
      toast.success(`Tags ${operationText} ${variables.channelIds.length} channels`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update tag assignments');
    }
  });
}

export function useRemoveAllTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeAllTagsFromChannels,
    onSuccess: (_, channelIds) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['channel-tags'] });
      queryClient.invalidateQueries({ queryKey: ['tag-channels'] });
      toast.success(`All tags removed from ${channelIds.length} channels`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove tags');
    }
  });
}